import { VertoBye, VertoInfo, VertoInvite, BaseComponent, selectors, } from '@signalwire/core';
import RTCPeer from './RTCPeer';
import { stopStream, stopTrack, getUserMedia } from './utils/webrtcHelpers';
const DEFAULT_CALL_OPTIONS = {
    destinationNumber: 'room',
    remoteCallerName: 'Outbound Call',
    remoteCallerNumber: '',
    callerName: '',
    callerNumber: '',
    audio: true,
    video: { aspectRatio: 16 / 9 },
    useStereo: false,
    attach: false,
    screenShare: false,
    additionalDevice: false,
    userVariables: {},
    requestTimeout: 10 * 1000,
    autoApplyMediaParams: true,
    iceGatheringTimeout: 2 * 1000,
};
export class BaseConnection extends BaseComponent {
    constructor(options) {
        super(options);
        this.nodeId = '';
        /** @internal */
        this.gotEarly = false;
        /** @internal */
        this.doReinvite = false;
        /** @internal */
        this._eventsPrefix = 'video';
        this.state = 'new';
        this.prevState = 'new';
        this.options = Object.assign(Object.assign({}, DEFAULT_CALL_OPTIONS), options);
        this.setState('new');
        this.logger.debug('New Call with Options:', this.options);
        this.applyEmitterTransforms({ local: true });
        this.attachWorkers();
    }
    get id() {
        return this.__uuid;
    }
    get active() {
        return this.state === 'active';
    }
    get trying() {
        return this.state === 'trying';
    }
    get memberId() {
        return this._memberId;
    }
    get previewUrl() {
        return this._previewUrl;
    }
    get roomId() {
        return this._roomId;
    }
    get roomSessionId() {
        return this._roomSessionId;
    }
    get localStream() {
        return this.options.localStream;
    }
    get remoteStream() {
        return this.options.remoteStream;
    }
    get iceServers() {
        var _a, _b;
        return (_b = (_a = this.options) === null || _a === void 0 ? void 0 : _a.iceServers) !== null && _b !== void 0 ? _b : this.select(selectors.getIceServers);
    }
    /** @internal */
    get messagePayload() {
        const { destinationNumber, attach, callerName, callerNumber, remoteCallerName, remoteCallerNumber, userVariables, screenShare, additionalDevice, } = this.options;
        return {
            sessid: this.options.sessionid,
            dialogParams: {
                id: this.__uuid,
                destinationNumber,
                attach,
                callerName,
                callerNumber,
                remoteCallerName,
                remoteCallerNumber,
                userVariables,
                screenShare,
                additionalDevice,
            },
        };
    }
    get cameraId() {
        return this.peer ? this.peer.getDeviceId('video') : null;
    }
    get cameraLabel() {
        return this.peer ? this.peer.getDeviceLabel('video') : null;
    }
    get microphoneId() {
        return this.peer ? this.peer.getDeviceId('audio') : null;
    }
    get microphoneLabel() {
        return this.peer ? this.peer.getDeviceLabel('audio') : null;
    }
    /** @internal */
    get withAudio() {
        // TODO: use peer to check audio tracks
        return this.remoteStream
            ? this.remoteStream.getAudioTracks().length > 0
            : false;
    }
    /** @internal */
    get withVideo() {
        // TODO: use peer to check video tracks
        return this.remoteStream
            ? this.remoteStream.getVideoTracks().length > 0
            : false;
    }
    get localVideoTrack() {
        return this.peer.localVideoTrack;
    }
    get localAudioTrack() {
        return this.peer.localAudioTrack;
    }
    /**
     * @internal
     * Verto messages have to be wrapped into an execute
     * request and sent using the 'video.message' method.
     */
    vertoExecute(vertoMessage) {
        const params = {
            message: vertoMessage,
            node_id: this.nodeId,
        };
        if (vertoMessage.method === 'verto.invite') {
            if (this.options.screenShare) {
                /** Only being used for debugging purposes */
                params.subscribe = ['video.room.screenshare'];
            }
            else if (this.options.additionalDevice) {
                /** Only being used for debugging purposes */
                params.subscribe = ['video.room.additionaldevice'];
            }
            else {
                params.subscribe = this.getSubscriptions();
            }
        }
        return this.execute({
            method: 'video.message',
            params,
        });
    }
    /** @internal */
    onStateChange(component) {
        this.logger.debug('onStateChange', component);
        switch (component.state) {
            case 'hangup':
                this._hangup(component);
                break;
            default:
                this.setState(component.state);
                break;
        }
    }
    /** @internal */
    onRemoteSDP(component) {
        this.logger.debug('onRemoteSDP', component);
        if (component.remoteSDP) {
            this.peer.onRemoteSdp(component.remoteSDP);
        }
    }
    /** @internal */
    onRoomSubscribed(component) {
        this.logger.debug('onRoomSubscribed', component);
        this.nodeId = component.nodeId;
        this._roomId = component.roomId;
        this._roomSessionId = component.roomSessionId;
        this._memberId = component.memberId;
        this._previewUrl = component.previewUrl;
        /**
         * For screenShare/additionalDevice we're using
         * the `memberId` to namespace the object.
         **/
        if (this.options.additionalDevice || this.options.screenShare) {
            this._attachListeners(component.memberId);
        }
        else {
            this._attachListeners(component.roomSessionId);
        }
        // FIXME: Move to a better place when rework _attachListeners too.
        this.applyEmitterTransforms();
    }
    /** @internal */
    onVideoConstraints(component) {
        this.logger.debug('onVideoConstraints', component);
        if (component === null || component === void 0 ? void 0 : component.videoConstraints) {
            this.peer.applyMediaConstraints('video', component.videoConstraints);
        }
    }
    /** @internal */
    onAudioConstraints(component) {
        this.logger.debug('onAudioConstraints', component);
        if (component === null || component === void 0 ? void 0 : component.audioConstraints) {
            this.peer.applyMediaConstraints('audio', component.audioConstraints);
        }
    }
    updateCamera(constraints) {
        return this.updateConstraints({
            video: Object.assign({ aspectRatio: 16 / 9 }, constraints),
        });
    }
    updateMicrophone(constraints) {
        return this.updateConstraints({
            audio: constraints,
        });
    }
    /** @internal */
    manageSendersWithConstraints(constraints) {
        if (constraints.audio === false) {
            this.logger.info('Switching off the microphone');
            this.stopOutboundAudio();
        }
        if (constraints.video === false) {
            this.logger.info('Switching off the camera');
            this.stopOutboundVideo();
        }
        return constraints.audio || constraints.video;
    }
    /**
     * @internal
     */
    updateConstraints(constraints) {
        return new Promise(async (resolve, reject) => {
            try {
                this.logger.debug('updateConstraints trying constraints', this.__uuid, constraints);
                if (!Object.keys(constraints).length) {
                    return this.logger.warn('Invalid constraints:', constraints);
                }
                const shouldContinueWithUpdate = this.manageSendersWithConstraints(constraints);
                if (!shouldContinueWithUpdate) {
                    this.logger.debug('Either `video` and `audio` (or both) constraints were set to `false` so their corresponding senders (if any) were stopped');
                    return;
                }
                const newStream = await getUserMedia(constraints);
                this.logger.debug('updateConstraints got stream', newStream);
                if (!this.options.localStream) {
                    this.options.localStream = new MediaStream();
                }
                const { instance } = this.peer;
                const tracks = newStream.getTracks();
                for (let i = 0; i < tracks.length; i++) {
                    const newTrack = tracks[i];
                    this.logger.debug('updateConstraints apply track: ', newTrack);
                    const transceiver = instance
                        .getTransceivers()
                        .find(({ mid, sender, receiver }) => {
                        if (sender.track && sender.track.kind === newTrack.kind) {
                            this.logger.debug('Found transceiver by sender');
                            return true;
                        }
                        if (receiver.track && receiver.track.kind === newTrack.kind) {
                            this.logger.debug('Found transceiver by receiver');
                            return true;
                        }
                        if (mid === null) {
                            this.logger.debug('Found disassociated transceiver');
                            return true;
                        }
                        return false;
                    });
                    if (transceiver && transceiver.sender) {
                        this.logger.debug('updateConstraints FOUND - replaceTrack on it and on localStream');
                        await transceiver.sender.replaceTrack(newTrack);
                        this.logger.debug('updateConstraints replaceTrack SUCCESS');
                        this.options.localStream.getTracks().forEach((track) => {
                            var _a;
                            if (track.kind === newTrack.kind && track.id !== newTrack.id) {
                                this.logger.debug('updateConstraints stop old track and apply new one - ');
                                stopTrack(track);
                                (_a = this.options.localStream) === null || _a === void 0 ? void 0 : _a.removeTrack(track);
                            }
                        });
                        this.options.localStream.addTrack(newTrack);
                    }
                    else {
                        this.logger.debug('updateConstraints NOT FOUND - addTrack and start dancing!');
                        this.peer.type = 'offer';
                        this.doReinvite = true;
                        this.options.localStream.addTrack(newTrack);
                        instance.addTrack(newTrack, this.options.localStream);
                    }
                    this.logger.debug('updateConstraints Simply update mic/cam');
                    if (newTrack.kind === 'audio') {
                        this.options.micId = newTrack.getSettings().deviceId;
                    }
                    else if (newTrack.kind === 'video') {
                        this.options.camId = newTrack.getSettings().deviceId;
                    }
                }
                this.logger.debug('updateConstraints done!');
                resolve();
            }
            catch (error) {
                this.logger.error('updateConstraints', error);
                reject(error);
            }
        });
    }
    /** @internal */
    invite() {
        return new Promise(async (resolve, reject) => {
            this.direction = 'outbound';
            this.peer = new RTCPeer(this, 'offer');
            try {
                await this.peer.start();
                resolve(this);
            }
            catch (error) {
                this.logger.error('Invite error', error);
                reject(error);
            }
        });
    }
    /** @internal */
    answer() {
        return new Promise(async (resolve, reject) => {
            this.direction = 'inbound';
            this.peer = new RTCPeer(this, 'answer');
            try {
                await this.peer.start();
                resolve(this);
            }
            catch (error) {
                this.logger.error('Answer error', error);
                reject(error);
            }
        });
    }
    /** @internal */
    onLocalSDPReady(localDescription) {
        const { type, sdp } = localDescription;
        const mungedSDP = this._mungeSDP(sdp);
        this.logger.debug('MUNGED SDP \n', `Type: ${type}`, '\n\n', mungedSDP);
        switch (type) {
            case 'offer':
                // if (this.active) {
                //   this.executeUpdateMedia()
                // } else {
                return this.executeInvite(mungedSDP);
            // }
            case 'answer':
                this.logger.warn('Unhandled verto.answer');
                // this.executeAnswer()
                break;
            default:
                return this.logger.error(`Unknown SDP type: '${type}' on call ${this.id}`);
        }
    }
    /** @internal */
    async executeInvite(sdp) {
        this.setState('requesting');
        try {
            const msg = VertoInvite(Object.assign(Object.assign({}, this.messagePayload), { sdp }));
            const response = await this.vertoExecute(msg);
            this.logger.debug('Invite response', response);
        }
        catch (error) {
            this.setState('hangup');
            throw error.jsonrpc;
        }
    }
    async hangup() {
        try {
            const bye = VertoBye(this.messagePayload);
            await this.vertoExecute(bye);
        }
        catch (error) {
            this.logger.error('Hangup error:', error);
        }
        finally {
            this._hangup();
        }
    }
    /** @internal */
    dtmf(dtmf) {
        const msg = VertoInfo(Object.assign(Object.assign({}, this.messagePayload), { dtmf }));
        this.vertoExecute(msg);
    }
    /** @internal */
    doReinviteWithRelayOnly() {
        if (this.peer && this.active) {
            this.peer.restartIceWithRelayOnly();
        }
    }
    /** @internal */
    stopOutboundAudio() {
        if (this.peer && this.active) {
            this.peer.stopTrackSender('audio');
        }
    }
    /** @internal */
    restoreOutboundAudio() {
        if (this.peer && this.active) {
            this.peer.restoreTrackSender('audio');
        }
    }
    /** @internal */
    stopOutboundVideo() {
        if (this.peer && this.active) {
            this.peer.stopTrackSender('video');
        }
    }
    /** @internal */
    restoreOutboundVideo() {
        if (this.peer && this.active) {
            this.peer.restoreTrackSender('video');
        }
    }
    /** @internal */
    setState(state) {
        this.prevState = this.state;
        this.state = state;
        this.logger.debug(`Call ${this.id} state change from ${this.prevState} to ${this.state}`);
        // @ts-expect-error
        this.emit(this.state, this);
        switch (state) {
            case 'purge': {
                this._finalize();
                break;
            }
            case 'hangup': {
                this.setState('destroy');
                break;
            }
            case 'destroy':
                this._finalize();
                break;
        }
    }
    /** @internal */
    _hangup(params = {}) {
        const { byeCause = 'NORMAL_CLEARING', byeCauseCode = '16', redirectDestination, } = params;
        this.cause = byeCause;
        this.causeCode = byeCauseCode;
        if (redirectDestination && this.peer.localSdp) {
            this.logger.debug('Redirect Destination to:', redirectDestination);
            this.nodeId = redirectDestination;
            return this.executeInvite(this.peer.localSdp);
        }
        return this.setState('hangup');
    }
    /**
     * Allow to define logic to munge the SDP
     *
     * @internal
     * */
    _mungeSDP(sdp) {
        return sdp;
    }
    /** @internal */
    _finalize() {
        if (this.peer && this.peer.instance) {
            this.peer.instance.close();
            // @ts-ignore
            delete this.peer;
        }
        const { remoteStream, localStream } = this.options;
        stopStream(remoteStream);
        stopStream(localStream);
        this.destroy();
    }
}
//# sourceMappingURL=BaseConnection.js.map